import {Component, HostListener, OnDestroy, OnInit} from '@angular/core';
import {TranslateService} from "@ngx-translate/core";
import {AuthService} from "./shared/auth.service";
import {User} from "./interfaces/user";
import {NbDateService, NbIconLibraries, NbSidebarService, NbThemeService, NbMediaBreakpoint} from "@nebular/theme";
import {NavigationEnd, Router} from "@angular/router";
import {filter} from "rxjs/operators";
import icons from '../assets/icons.json';
import lucideIcons from '../assets/lucide.json';
import {SubscribedComponent} from "./subscribed.component";
import {Globals} from "./shared/Globals";
import {Subscription} from "rxjs";
import {TitleService} from "./shared/title.service";
import {SettingsService} from "./shared/settings.service";
import {TimeTrackingService} from "./shared/time-tracking.service";

@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.scss'],
    standalone: false
})
export class AppComponent implements OnInit, OnDestroy{

  @HostListener('window:beforeunload')
  onBeforeUnload(): void{
    if(this.auth.loggedIn) {
      this.settings.saveUserSettings();
    }
  }

  public user: User;
  public isMobile = false;
  public sidebarOpen = false;
  // must stay in sync with the sidebar's [collapsedBreakpoints] in app.component.html
  private readonly mobileBreakpoints = ['xs', 'is', 'sm'];
  private subs: Subscription[] = [];
  private trackingtitle: boolean = false;

  constructor(public translate: TranslateService,
              private iconService: NbIconLibraries,
              private titleService: TitleService,
              private settings: SettingsService,
              private trackingService: TimeTrackingService,
              private auth: AuthService,
              private sidebarService: NbSidebarService,
              private themeService: NbThemeService,
              private dateService: NbDateService<unknown>,
              private router: Router) {

    console.log('Starting v' + Globals.version);
    // APP_INITIALIZER has already loaded the user's settings by now.
    this.applyLanguage();

    this.iconService.registerSvgPack('custom-icons', icons);
    this.iconService.registerSvgPack('lucide', lucideIcons);
  }

  // The language the user picked in the header (stored in their settings), else the guest's
  // pick on this browser, else the default.
  // A saved code we no longer ship would 404 its translations and blank the UI, so it falls back too.
  private applyLanguage(): void {
    let guest: string = null;
    try { guest = localStorage.getItem(Globals.GUEST_LANGUAGE_KEY); } catch {}
    const saved = this.settings.userSetting('language') || guest;
    const lang = Globals.LANGUAGES.includes(saved) ? saved : this.translate.defaultLang;
    this.translate.use(lang);
    // NbMomentDateService (datepicker day/month parsing) has no link to the app's own
    // language selection otherwise, so sync it here, and again in header.component.ts's
    // setLanguage() whenever the user actually switches (#1175).
    this.dateService.setLocale(lang);
  }

  private lastLoading: Subscription = null;
  async onActivate(comp: any) {
    if (this.lastLoading) {
      this.lastLoading.unsubscribe();
      this.lastLoading = null;
      // The left page can't report its loading=false any more; a page that isn't a
      // SubscribedComponent never emits one either, so drop its flag here.
      Globals.removeLoading();
    }

    console.log(comp);
    if(comp instanceof SubscribedComponent) {
      this.lastLoading = (<SubscribedComponent>comp).loading.subscribe((is) => {
        if (is) {
          Globals.addLoading();
        } else {
          Globals.removeLoading();
        }
      });
    }

    // Every page resets the breadcrumb, not only SubscribedComponents, or the last one leaks.
    const title = comp?.headerTitle;
    if(title && typeof title === 'string') {
      await this.titleService.newTitle(title);
    }
    else if(title && typeof title === 'object'){
      await this.titleService.newTitle(title.title, title.translate);
    }
    else{
      await this.titleService.newTitle();
    }
  }

  ngOnDestroy(): void {
    if(this.subs){
      this.subs.forEach((sub) => {
        sub.unsubscribe();
      });
      this.subs = [];
    }
  }

  ngOnInit(): void {
    this.subs.push(this.auth.user.subscribe((user) => {
      this.user = user || null;
      // A login or company switch without a page reload brings different settings, and a
      // logout clears them, so the login page goes back to the guest's pick or the default.
      this.applyLanguage();
      if (!user) { this.sidebarOpen = false; return; }
      // Desktop sidebar used to rest as an icons-only rail (expands on hover),
      // but is now always expanded. The sidebar mounts after the user resolves,
      // so defer to the next tick if needed.
      setTimeout(() => { if (this.user && !this.isMobile) { this.sidebarService.expand('menu'); } });
    }));

    this.subs.push(this.trackingService.timer.subscribe(async (t) => {
      if(t && t.tracked_durationString){
        await this.setTitleTime(t.tracked_durationString);
        this.trackingtitle = true;
      }
      else if(this.trackingtitle){
        await this.setTitleTime(null);
        this.trackingtitle = false;
      }
    }));

    // Seed before the first breakpoint event (768 = md boundary); refined by name below.
    this.isMobile = window.innerWidth < 768;

    this.subs.push(this.themeService.onMediaQueryChange()
      .subscribe(([, current]: NbMediaBreakpoint[]) => {
        this.isMobile = this.mobileBreakpoints.includes(current.name);
        // Nebular's responsive auto-collapse doesn't emit a service event,
        // so reset our own flag on every breakpoint crossing.
        this.sidebarOpen = false;
        // Desktop: keep the sidebar expanded. Mobile keeps the
        // responsive overlay drawer (#445), so don't expand/compact there.
        if (!this.isMobile) { this.sidebarService.expand('menu'); }
      }));

    this.subs.push(this.sidebarService.onToggle().subscribe(({tag}) => {
      if (tag === 'menu') { this.sidebarOpen = !this.sidebarOpen; }
    }));
    this.subs.push(this.sidebarService.onExpand().subscribe(({tag}) => {
      if (tag === 'menu') { this.sidebarOpen = true; }
    }));
    this.subs.push(this.sidebarService.onCollapse().subscribe(({tag}) => {
      if (tag === 'menu') { this.sidebarOpen = false; }
    }));

    this.subs.push(this.router.events
      .pipe(filter(e => e instanceof NavigationEnd))
      .subscribe(() => {
        if (this.isMobile && this.sidebarOpen) { this.closeSidebar(); }
      }));
  }

  closeSidebar(): void {
    this.sidebarService.collapse('menu');
    this.sidebarOpen = false;
  }

  skipToContent(event: Event): void {
    // <base href="/"> would otherwise send an in-page hash link to the home route.
    event.preventDefault();
    document.getElementById('main-content')?.focus();
  }

  @HostListener('document:keydown.escape')
  closeMobileNavigation(): void {
    if (this.isMobile && this.sidebarOpen) {
      this.closeSidebar();
      document.querySelector<HTMLButtonElement>('.hamburger')?.focus();
    }
  }

  private async setTitleHeader(header: string = Globals.APP_NAME, translate: boolean = false) {
    let cur = this.titleService.title;
    if(this.trackingtitle){
      const parts = cur.split(' ', 1);
      if(parts[1]){
        cur = parts[0] + ' ' + header;
      }
    }
    else{
      cur = header;
    }
    await this.titleService.newTitle(cur, translate);
  }

  private async setTitleTime(time: string = null){
    const reg = /^[0-9]*?:[0-9][0-9]:[0-9][0-9]/g;
    let cur = this.titleService.title ?? '';

    const matches = cur.match(reg);
    if (matches?.length) {
      cur = cur.replace(reg, time || '');
    }
    else if(time){
      cur = time + ' ' + cur;
    }
    await this.titleService.newTitle(cur.trim());
  }
}
